/**
 * ConfirmDialog — a small, centered modal for confirming a (usually
 * destructive) action. The codebase had no dialog/alert-dialog primitive —
 * destructive flows used `window.confirm` or inline two-click confirms — so
 * this gives a consistent, token-styled confirm surface for the delete
 * features (Equipment / Gateways / process-graph bulk delete, Admin danger
 * zone, platform-console org delete).
 *
 * Two exports:
 *   - {@link ConfirmDialog}      — title + body + Confirm / Cancel.
 *   - {@link DangerConfirmDialog} — adds a typed-confirmation gate: the user
 *     must type an exact phrase (e.g. the org name) before Confirm enables.
 *     Used for the irreversible "Delete organisation" flow.
 *
 * Both render as a full-viewport fixed overlay with a click-to-close backdrop;
 * ESC closes. Mirrors the Sheet primitive's structure but center-anchored.
 */

import { useEffect, useId, useState, type ReactNode } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { cn } from "./cn";
import { Button } from "./button";
import { Input } from "./input";

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  /** Fired when the user confirms. May be async; the dialog shows a spinner
   *  while the returned promise is pending and closes on resolve. */
  onConfirm: () => void | Promise<void>;
  title: string;
  /** Body — string or arbitrary nodes (lists, emphasis, …). */
  description?: ReactNode;
  /** Confirm button label. Default "Confirm". */
  confirmLabel?: string;
  /** Cancel button label. Default "Cancel". */
  cancelLabel?: string;
  /** Style the confirm button as destructive. Default false. */
  destructive?: boolean;
}

/**
 * Shared overlay shell — backdrop + centered card + ESC-to-close. Not
 * exported; both dialogs render through it.
 */
function DialogShell({
  open,
  onClose,
  ariaLabel,
  children,
}: {
  open: boolean;
  onClose: () => void;
  ariaLabel: string;
  children: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    // Capture phase + preventDefault: Esc closes this dialog only, not a Sheet
    // it was opened over (the Sheet ignores an Esc already handled).
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-on-surface/40 backdrop-blur-sm"
      />
      <div className="relative flex w-full max-w-md flex-col gap-4 rounded-lg border border-border bg-surface-elevated p-5 text-on-surface shadow-2xl animate-fade-in">
        {children}
      </div>
    </div>
  );
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
}: ConfirmDialogProps) {
  const [busy, setBusy] = useState(false);

  // Reset the in-flight guard whenever the dialog is (re)opened.
  useEffect(() => {
    if (open) setBusy(false);
  }, [open]);

  const confirm = async () => {
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogShell open={open} onClose={onClose} ariaLabel={title}>
      <div className="flex items-start gap-3">
        {destructive && (
          <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-danger/10 text-danger">
            <AlertTriangle className="size-5" />
          </div>
        )}
        <div className="flex min-w-0 flex-col gap-1">
          <h2 className="text-base font-semibold leading-tight text-on-surface">
            {title}
          </h2>
          {description && (
            <div className="text-sm leading-relaxed text-on-surface-muted">
              {description}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
          {cancelLabel}
        </Button>
        <Button
          variant={destructive ? "danger" : "primary"}
          size="sm"
          onClick={() => void confirm()}
          disabled={busy}
        >
          {busy && <Loader2 className="size-4 animate-spin" />}
          {confirmLabel}
        </Button>
      </div>
    </DialogShell>
  );
}

export interface DangerConfirmDialogProps
  extends Omit<ConfirmDialogProps, "destructive"> {
  /**
   * The exact phrase the user must type (case-sensitive, trimmed) before the
   * Confirm button enables — typically the org name. Guards an irreversible
   * action against accidental clicks.
   */
  confirmPhrase: string;
  /** Label above the input. Default `Type "<phrase>" to confirm`. */
  inputLabel?: string;
}

/**
 * Typed-confirmation variant for irreversible actions (delete-org). The
 * Confirm button stays disabled until the typed value matches
 * `confirmPhrase` exactly. Always destructive-styled.
 */
export function DangerConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmPhrase,
  inputLabel,
  confirmLabel = "Delete",
  cancelLabel = "Cancel",
}: DangerConfirmDialogProps) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const inputId = useId();

  // Clear the typed value + in-flight guard on each open so a reopened dialog
  // never starts pre-armed.
  useEffect(() => {
    if (open) {
      setValue("");
      setBusy(false);
    }
  }, [open]);

  const matches = value.trim() === confirmPhrase;

  const confirm = async () => {
    if (!matches) return;
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogShell open={open} onClose={onClose} ariaLabel={title}>
      <div className="flex items-start gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-danger/10 text-danger">
          <AlertTriangle className="size-5" />
        </div>
        <div className="flex min-w-0 flex-col gap-1">
          <h2 className="text-base font-semibold leading-tight text-on-surface">
            {title}
          </h2>
          {description && (
            <div className="text-sm leading-relaxed text-on-surface-muted">
              {description}
            </div>
          )}
        </div>
      </div>

      <label htmlFor={inputId} className="flex flex-col gap-1.5">
        <span className="text-xs font-medium text-on-surface-muted">
          {inputLabel ?? (
            <>
              Type <span className="font-mono text-on-surface">{confirmPhrase}</span>{" "}
              to confirm
            </>
          )}
        </span>
        <Input
          id={inputId}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && matches && !busy) {
              e.preventDefault();
              void confirm();
            }
          }}
          placeholder={confirmPhrase}
          autoFocus
          autoComplete="off"
          className={cn(matches && "border-danger focus-visible:ring-danger")}
        />
      </label>

      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
          {cancelLabel}
        </Button>
        <Button
          variant="danger"
          size="sm"
          onClick={() => void confirm()}
          disabled={!matches || busy}
        >
          {busy && <Loader2 className="size-4 animate-spin" />}
          {confirmLabel}
        </Button>
      </div>
    </DialogShell>
  );
}
